import { useState, useEffect, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Zap, CheckCircle, AlertCircle, Loader2, Check, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { useIntegrationStore } from '../../store/integrationStore';
import { usePersonStore } from '../../store/personStore';
import api from '../../lib/api';
import { BlastSchoolTypeahead } from '../common';
import type { PersonDetail, BlastSchool, BlastTeam, BlastUser, ConvertResponse } from '../../types';
import { mapCrmGenderToBlastGender, mapCrmLevelToBlastLevel, blastGenderLabel } from '../../lib/blastLevelGenderMap';

export interface ConvertInitialTeamContext {
  organizationId: string;
  organizationName: string;
  organizationCity: string;
  organizationState: string;
  organizationAddress: string;
  organizationZip: string;
  blastSchoolId: string | null;
  sportName: string;
  sportGender: 'BOYS' | 'GIRLS' | 'COED';
  teamLevel: string;
  blastTeamId: string | null;
}

interface ConvertToBlastModalProps {
  isOpen: boolean;
  onClose: () => void;
  person: PersonDetail;
  onSuccess: () => void;
  initialTeamContext?: ConvertInitialTeamContext;
}

type Step = 'school' | 'team' | 'coach' | 'review' | 'result';

type SchoolMode = 'confirmed' | 'suggested' | 'manual';

const STEPS: Step[] = ['school', 'team', 'coach', 'review', 'result'];
const STEP_LABELS: Record<Step, string> = {
  school: 'School',
  team: 'Team',
  coach: 'Coach',
  review: 'Review',
  result: 'Result',
};

export const ConvertToBlastModal = ({
  isOpen,
  onClose,
  person,
  onSuccess,
  initialTeamContext,
}: ConvertToBlastModalProps) => {
  const { user } = useAuthStore();
  const {
    convertPerson,
    isConverting,
    searchBlastSchools,
    fetchOrgBlastTeams,
    fetchBlastUserByEmail,
    orgBlastTeamsData,
    isLoadingTeams,
    clearOrgBlastTeams,
    fetchApprovedTeams,
    approvedTeamsData,
    clearApprovedTeams,
    blastSports,
    fetchBlastSports,
    isLoadingSports,
  } = useIntegrationStore();

  // CRM-28: split current vs past. The browse picker shows CURRENT teams only;
  // `pastTeams` is kept ONLY for the exact blastTeamId pre-select match below, so
  // an already-created team — even a past one — never produces a duplicate.
  const currentTeams: BlastTeam[] = orgBlastTeamsData
    ? orgBlastTeamsData.current.flatMap(g =>
        g.teams.map(t => ({ id: t.id, name: t.name, sport_id: g.sportId, level: t.level, level_name: t.levelName, gender: t.gender, school_year: t.schoolYear }))
      )
    : [];
  const pastTeams: BlastTeam[] = orgBlastTeamsData
    ? orgBlastTeamsData.past.flatMap(g =>
        g.teams.map(t => ({ id: t.id, name: t.name, sport_id: t.sportId, level: t.level, level_name: t.levelName, gender: t.gender, school_year: t.schoolYear }))
      )
    : [];
  const allBlastTeams: BlastTeam[] = [...currentTeams, ...pastTeams];
  const { updatePerson } = usePersonStore();

  // Idempotency key — generated once per modal open
  const idempotencyKeyRef = useRef<string>('');

  // Step state
  const [currentStep, setCurrentStep] = useState<Step>('school');

  // School step
  const [schoolMode, setSchoolMode] = useState<SchoolMode>('manual');
  const [selectedBlastSchool, setSelectedBlastSchool] = useState<BlastSchool | null>(null);
  const [schoolName, setSchoolName] = useState('');
  const [schoolCity, setSchoolCity] = useState('');
  const [schoolState, setSchoolState] = useState('');
  const [suggestedSchools, setSuggestedSchools] = useState<BlastSchool[]>([]);
  const [isSearchingSuggestions, setIsSearchingSuggestions] = useState(false);

  // New school fields (for Blast creation)
  const [schoolAddress, setSchoolAddress] = useState('');
  const [schoolZip, setSchoolZip] = useState('');
  const [schoolType, setSchoolType] = useState<'highschool' | 'college'>('highschool');

  // Team step
  const [selectedBlastTeam, setSelectedBlastTeam] = useState<BlastTeam | null>(null);
  const [isNewTeam, setIsNewTeam] = useState(false);
  const [showAllCurrentTeams, setShowAllCurrentTeams] = useState(false);
  const [selectedSportName, setSelectedSportName] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('varsity');
  const [selectedGender, setSelectedGender] = useState('mw');
  const [selectedSchoolYear, setSelectedSchoolYear] = useState<number>(() => {
    const now = new Date();
    return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  });

  // Coach step
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [existingBlastUser, setExistingBlastUser] = useState<BlastUser | null>(null);
  const [isCheckingCoach, setIsCheckingCoach] = useState(false);
  const coachCheckedRef = useRef(false);
  const teamStepInitializedRef = useRef(false);

  // Result step
  const [convertResult, setConvertResult] = useState<ConvertResponse | null>(null);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);

  // Derive primary org context once
  const primaryOrg = person.organizations?.find((o) => o.isPrimary) || person.organizations?.[0];
  const orgHasBlastSchoolId = !!(initialTeamContext?.blastSchoolId ?? primaryOrg?.organization.blastSchoolId);

  // Track previous isOpen to only reset on closed → open transition
  const wasOpenRef = useRef(false);

  // Reset form when modal opens (not when person data changes mid-flow)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      // crypto.randomUUID() requires secure context (HTTPS or localhost).
      // Fall back to a manual UUID for non-secure contexts (e.g., LAN IP access).
      idempotencyKeyRef.current = typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      setCurrentStep('school');
      setConvertResult(null);
      setConvertError(null);
      setErrorCode(null);

      // Reset school state
      setSelectedBlastSchool(null);
      setSuggestedSchools([]);
      setIsSearchingSuggestions(false);

      // Reset team state
      setSelectedBlastTeam(null);
      setIsNewTeam(false);
      setShowAllCurrentTeams(false);
      clearOrgBlastTeams();
      clearApprovedTeams();

      // Reset coach state
      setExistingBlastUser(null);
      setIsCheckingCoach(false);
      coachCheckedRef.current = false;
      teamStepInitializedRef.current = false;

      // Pre-fill from person data
      const org = person.organizations?.find((o) => o.isPrimary) || person.organizations?.[0];
      setSchoolName(org?.organization.name || '');
      setSchoolCity(org?.organization.city || '');
      setSchoolState(org?.organization.state || '');
      setSchoolAddress(org?.organization.address || '');
      setSchoolZip(org?.organization.zip || '');
      setSchoolType('highschool');

      // Fetch sports for dropdown if not already loaded
      if (blastSports.length === 0) fetchBlastSports();

      // Pre-fill team from person's linked sport
      const personSport = person.organizations?.find((o) => o.sport)?.sport;
      setSelectedSportName(personSport?.name || '');
      setSelectedLevel('varsity');
      setSelectedGender(personSport?.gender ? mapCrmGenderToBlastGender(personSport.gender) : 'mw');
      const nowForYear = new Date();
      setSelectedSchoolYear(nowForYear.getMonth() >= 6 ? nowForYear.getFullYear() + 1 : nowForYear.getFullYear());

      // Pre-fill coach from person
      setFirstName(person.firstName);
      setLastName(person.lastName);
      setEmail(person.email || '');
      setPhone(person.phone || '');

      // ─────────────────────────────────────────────────────────────
      // Override pre-fills from team-row context (when launched from
      // a TeamRow Convert pill on the Org page).
      // ─────────────────────────────────────────────────────────────
      if (initialTeamContext) {
        setSchoolName(initialTeamContext.organizationName);
        setSchoolCity(initialTeamContext.organizationCity);
        setSchoolState(initialTeamContext.organizationState);
        setSchoolAddress(initialTeamContext.organizationAddress);
        setSchoolZip(initialTeamContext.organizationZip);
        setSelectedSportName(initialTeamContext.sportName);
        setSelectedLevel(mapCrmLevelToBlastLevel(initialTeamContext.teamLevel));
        setSelectedGender(mapCrmGenderToBlastGender(initialTeamContext.sportGender));
      }

      // Determine school mode based on CRM context.
      // initialTeamContext (when present) takes precedence over person.primaryOrg.
      const ctxBlastSchoolId = initialTeamContext?.blastSchoolId ?? org?.organization.blastSchoolId ?? null;
      const ctxOrgName = initialTeamContext?.organizationName ?? org?.organization.name;
      const ctxOrgState = initialTeamContext?.organizationState ?? org?.organization.state ?? '';
      const ctxOrgCity = initialTeamContext?.organizationCity ?? org?.organization.city ?? '';

      if (ctxBlastSchoolId) {
        // Already linked to Blast — auto-confirm mode
        setSchoolMode('confirmed');
        setSelectedBlastSchool({
          id: Number(ctxBlastSchoolId),
          name: ctxOrgName ?? '',
          city: ctxOrgCity,
          state: ctxOrgState,
          school_type: '',
          nces_id: null,
          district: null,
          timezone: '',
        });
      } else if (ctxOrgName) {
        // Org exists but no Blast link — suggest mode
        setSchoolMode('suggested');
        searchSuggestedSchools(ctxOrgName, ctxOrgState);
      } else {
        // No org linked — manual mode
        setSchoolMode('manual');
      }
    } else if (!isOpen) {
      wasOpenRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Search Blast for suggested schools based on CRM org name/state
  const searchSuggestedSchools = async (name: string, state: string) => {
    setIsSearchingSuggestions(true);
    try {
      const results = await searchBlastSchools({
        name,
        state: state || undefined,
        limit: 10,
      });
      setSuggestedSchools(results);
    } catch {
      setSuggestedSchools([]);
    } finally {
      setIsSearchingSuggestions(false);
    }
  };

  // When entering team step, fetch existing Blast teams for the selected school
  const loadTeamsForSchool = async () => {
    if (selectedBlastSchool?.id) {
      await Promise.all([
        fetchOrgBlastTeams(selectedBlastSchool.id),
        fetchApprovedTeams(selectedBlastSchool.id),
      ]);
    }
  };

  // On team-step entry, initialize from initialTeamContext:
  //   - If blastTeamId set and matching Blast team exists → pre-select it
  //   - Otherwise (blastTeamId null, or set-but-no-match) → open in "new team" mode
  //     (level/gender/sport are already pre-filled by Insertion A in the init effect)
  // Ref guard ensures this only fires once per modal-open, so the rep can use
  // the existing "Back to existing teams" button without being flipped back.
  useEffect(() => {
    if (currentStep !== 'team' || !initialTeamContext || !orgBlastTeamsData) return;
    if (teamStepInitializedRef.current) return;
    teamStepInitializedRef.current = true;

    if (initialTeamContext.blastTeamId) {
      // CRM-28: match against current + past so an already-created team always
      // pre-selects (no duplicate), even when Blast classifies it as a past year.
      const matchingTeam = allBlastTeams.find(t => String(t.id) === initialTeamContext.blastTeamId);
      if (matchingTeam) {
        setSelectedBlastTeam(matchingTeam);
        setIsNewTeam(false);
        return;
      }
    }
    // blastTeamId is null OR set-but-no-match → default to "new team" form
    setIsNewTeam(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, orgBlastTeamsData, initialTeamContext]);

  // Reconcile the new-team seeds against the school's approved set (CRM-2).
  // When the approved option sets arrive (or the chosen sport changes): drop a
  // seeded sport the school hasn't approved, and clamp level/gender to a valid
  // approved member so a rep can't submit a non-approved combo. The BLA-15
  // server-side gate is the real guarantee; this just keeps the menu honest.
  useEffect(() => {
    if (!approvedTeamsData) return;
    const match = approvedTeamsData.sports.find((s) => s.name === selectedSportName);
    if (selectedSportName && !match) {
      setSelectedSportName('');
      return;
    }
    if (!match) return;
    const levels = approvedTeamsData.levelsBySport[match.blastSportId] ?? [];
    const genders = approvedTeamsData.gendersBySport[match.blastSportId] ?? [];
    if (levels.length && !levels.includes(selectedLevel)) setSelectedLevel(levels[0]);
    if (genders.length && !genders.includes(selectedGender)) setSelectedGender(genders[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedTeamsData, selectedSportName]);

  // When entering coach step, check if email exists in Blast
  const checkCoachInBlast = async () => {
    if (coachCheckedRef.current) return;
    coachCheckedRef.current = true;

    if (!email) {
      setExistingBlastUser(null);
      return;
    }

    setIsCheckingCoach(true);
    try {
      const blastUser = await fetchBlastUserByEmail(email);
      setExistingBlastUser(blastUser);
    } catch {
      setExistingBlastUser(null);
    } finally {
      setIsCheckingCoach(false);
    }
  };

  const currentStepIndex = STEPS.indexOf(currentStep);

  const canGoNext = (): boolean => {
    switch (currentStep) {
      case 'school':
        if (schoolMode === 'confirmed') return true;
        if (schoolMode === 'suggested') {
          // Must have picked a suggestion (which switches to confirmed) or gone to manual
          return false;
        }
        // Manual: require all fields
        if (selectedBlastSchool) return true;
        return !!(schoolName && schoolCity && schoolState && schoolAddress && schoolZip && schoolType);
      case 'team': {
        if (selectedBlastTeam && !isNewTeam) return true;
        // Only treat the pre-filled new-team fields as a valid choice when the
        // new-team form is actually showing (school has no Blast teams, or the
        // rep explicitly clicked "New sport/team"). Otherwise the rep is looking
        // at the existing-teams list and hasn't picked anything yet.
        const newTeamFormVisible = isNewTeam || (pickerTeams.length === 0 && !selectedBlastTeam);
        return newTeamFormVisible && !!(selectedSportName && selectedLevel && selectedGender);
      }
      case 'coach':
        return !!(firstName && lastName && email);
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (currentStep === 'review') {
      handleConvert();
      return;
    }
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      const nextStep = STEPS[nextIndex];

      // Trigger side effects when entering specific steps
      if (nextStep === 'team') {
        // Reset team selection when entering team step
        setSelectedBlastTeam(null);
        setIsNewTeam(false);
        await loadTeamsForSchool();
      }
      if (nextStep === 'coach') {
        // Trigger coach lookup on step entry
        coachCheckedRef.current = false;
        setExistingBlastUser(null);
        checkCoachInBlast();
      }
      if (nextStep === 'review') {
        // Re-check coach if email was edited on the coach step
        coachCheckedRef.current = false;
        await checkCoachInBlast();
      }

      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex]);
    }
  };

  const handleConvert = async () => {
    if (!user) {
      setConvertError('You must be logged in to convert.');
      setCurrentStep('result');
      return;
    }

    try {
      const result = await convertPerson(
        {
          school: {
            blast_school_id: selectedBlastSchool?.id || undefined,
            name: schoolName,
            city: schoolCity,
            state: schoolState,
            ...(selectedBlastSchool ? {} : {
              address: schoolAddress,
              zip: schoolZip,
              school_type: schoolType,
            }),
          },
          team: {
            blast_team_id: (selectedBlastTeam && !isNewTeam) ? selectedBlastTeam.id : undefined,
            ...((selectedBlastTeam && !isNewTeam) ? {} : {
              sport_name: selectedSportName,
              level: selectedLevel,
              gender: selectedGender,
              school_year: selectedSchoolYear,
            }),
          },
          coach: {
            first_name: firstName,
            last_name: lastName,
            email,
            phone: phone ? phone.replace(/[\s\-().]/g, '') : undefined,
          },
          crmPersonId: person.id,
        },
        idempotencyKeyRef.current
      );

      setConvertResult(result);
      setConvertError(null);
      setCurrentStep('result');

      // Mark person as converted; set blastUserId if Blast returned a coach ID
      try {
        const updates: Record<string, unknown> = { convertedAt: new Date().toISOString() };
        if (result.coach.id) {
          updates.blastUserId = String(result.coach.id);
        }
        await updatePerson(person.id, updates);
      } catch {
        console.error('Failed to update person after convert');
      }

      // Link org to Blast school if we have an org and got a school ID back
      if (primaryOrg && result.school.id) {
        try {
          await api.patch(`/api/organizations/${primaryOrg.organization.id}`, {
            blastSchoolId: String(result.school.id),
          });
        } catch {
          console.error('Failed to update org blastSchoolId after convert');
        }
      }
    } catch (error: any) {
      const status = error.response?.status;
      setErrorCode(status || null);

      if (status === 403) {
        setConvertError("You don't have permission to convert for this school.");
      } else if (status === 409) {
        setConvertError('Multiple schools matched — select a specific school.');
      } else if (status === 400) {
        const msg = error.response?.data?.error?.message
          || error.response?.data?.message
          || 'Bad request — check the form data.';
        setConvertError(msg);
      } else {
        setConvertError(
          error.response?.data?.error?.message
          || error.response?.data?.message
          || 'Convert operation failed. Please try again.'
        );
      }
      setConvertResult(null);
      setCurrentStep('result');
    }
  };

  const handleClose = () => {
    if (convertResult) {
      onSuccess();
    } else {
      onClose();
    }
  };

  // Helper: pick a suggested school
  const handlePickSuggestion = (school: BlastSchool) => {
    setSelectedBlastSchool(school);
    setSchoolName(school.name);
    setSchoolCity(school.city);
    setSchoolState(school.state);
    setSchoolMode('confirmed');
  };

  // Helper: switch to manual search from suggestions
  const handleManualSearch = () => {
    setSchoolMode('manual');
    setSelectedBlastSchool(null);
  };

  // Helper: pick an existing Blast team
  const handlePickTeam = (team: BlastTeam) => {
    setSelectedBlastTeam(team);
    setIsNewTeam(false);
    setSelectedSportName(''); // Not needed — blast_team_id path
    setSelectedLevel(team.level);
    setSelectedGender(team.gender);
    setSelectedSchoolYear(team.school_year);
  };

  // Helper: choose to create a new team
  const handleNewTeam = () => {
    setIsNewTeam(true);
    setSelectedBlastTeam(null);
    const personSport = person.organizations?.find((o) => o.sport)?.sport;
    setSelectedSportName(initialTeamContext?.sportName || personSport?.name || '');
    setSelectedLevel(initialTeamContext ? mapCrmLevelToBlastLevel(initialTeamContext.teamLevel) : 'varsity');
    setSelectedGender(
      initialTeamContext
        ? mapCrmGenderToBlastGender(initialTeamContext.sportGender)
        : personSport?.gender
          ? mapCrmGenderToBlastGender(personSport.gender)
          : 'mw'
    );
  };

  if (!isOpen) return null;

  // Level/gender display option lists
  const LEVEL_OPTIONS = [
    { value: 'program', label: 'Program' },
    { value: 'varsity', label: 'Varsity' },
    { value: 'jrvarsity', label: 'Jr. Varsity' },
    { value: 'freshman', label: 'Freshman' },
    { value: 'froshsoph', label: 'Frosh/Soph' },
    { value: 'sophomore', label: 'Sophomore' },
  ];

  const GENDER_OPTIONS = [
    { value: 'mw', label: 'Boys & Girls (Coed)' },
    { value: 'm', label: 'Boys' },
    { value: 'w', label: 'Girls' },
  ];

  // Approved-set narrowing (CRM-2): when the school's approved set is loaded,
  // drive the sport list and the level/gender membership from it; otherwise
  // fall back to the full global lists (graceful degradation if the endpoint is
  // unavailable — the BLA-15 server-side gate still enforces correctness).
  const approvedSportList = approvedTeamsData?.sports ?? null;
  const sportSelectOptions =
    approvedSportList && approvedSportList.length
      ? approvedSportList
      : blastSports.map((s) => ({ blastSportId: s.blastSportId, name: s.name }));
  const selectedSportId =
    sportSelectOptions.find((s) => s.name === selectedSportName)?.blastSportId ?? null;
  const approvedLevels =
    approvedTeamsData && selectedSportId != null ? approvedTeamsData.levelsBySport[selectedSportId] : undefined;
  const approvedGenders =
    approvedTeamsData && selectedSportId != null ? approvedTeamsData.gendersBySport[selectedSportId] : undefined;
  const levelOptions =
    approvedLevels && approvedLevels.length
      ? LEVEL_OPTIONS.filter((l) => approvedLevels.includes(l.value))
      : LEVEL_OPTIONS;
  const genderOptions =
    approvedGenders && approvedGenders.length
      ? GENDER_OPTIONS.filter((g) => approvedGenders.includes(g.value))
      : GENDER_OPTIONS;

  // CRM-28: scope the existing-team picker to the contact's current-year sport.
  // `selectedSportId` is the Blast sport id for the currently-seeded sport (from
  // the contact's sport or the team context). Gender-narrow only when the seed is
  // a specific m/w; a coed/'mw' seed falls back to sport-only so we never hide a
  // valid team (also keep coed teams visible for a single-gender coach).
  //
  // The scoping gender is the contact's *original* sport gender (the team-context
  // gender when launched from a TeamRow), NOT the live `selectedGender` — the
  // latter is reclamped by the CRM-2 approved-set cascade (e.g. a coed Basketball
  // coach at a school approved only for m/w gets clamped to 'm'), which would
  // otherwise wrongly hide the other single-gender team for a coed contact.
  const scopeSportGender = person.organizations?.find((o) => o.sport)?.sport?.gender;
  const scopeGender = initialTeamContext
    ? mapCrmGenderToBlastGender(initialTeamContext.sportGender)
    : scopeSportGender
      ? mapCrmGenderToBlastGender(scopeSportGender)
      : 'mw';
  const scopedTeams =
    selectedSportId == null
      ? currentTeams
      : currentTeams.filter((t) => {
          if (t.sport_id !== selectedSportId) return false;
          if (scopeGender === 'mw') return true;
          return t.gender === scopeGender || t.gender === 'mw';
        });
  const pickerTeams = showAllCurrentTeams ? currentTeams : scopedTeams;
  const hasMoreCurrentTeams = currentTeams.length > scopedTeams.length;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div data-testid="convert-modal" className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Zap className="h-5 w-5 text-blast-600" />
              Convert to Blast
            </h2>
            <button
              data-testid="convert-modal-close"
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Progress bar */}
          {currentStep !== 'result' && (
            <div className="px-6 pt-4">
              <div className="flex items-center gap-1">
                {STEPS.filter((s) => s !== 'result').map((step, i) => (
                  <div key={step} className="flex items-center flex-1">
                    <div
                      className={`h-1.5 w-full rounded-full ${
                        i <= currentStepIndex
                          ? 'bg-blast-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-1">
                {STEPS.filter((s) => s !== 'result').map((step) => (
                  <span
                    key={step}
                    className={`text-xs ${
                      step === currentStep
                        ? 'text-blast-600 font-medium'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {STEP_LABELS[step]}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Body */}
          <div className="p-6 space-y-4 min-h-[280px]">
            {/* ==================== SCHOOL STEP ==================== */}
            {currentStep === 'school' && (
              <div className="space-y-4">
                {/* Mode: Confirmed (org has blastSchoolId or user picked a suggestion) */}
                {schoolMode === 'confirmed' && selectedBlastSchool && (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      School linked to Blast.
                    </p>
                    <div data-testid="convert-school-confirmed-card" className="border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg p-4 flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {selectedBlastSchool.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {selectedBlastSchool.city}, {selectedBlastSchool.state}
                        </p>
                        <span className="inline-block mt-1 text-xs font-mono bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                          Blast #{selectedBlastSchool.id}
                        </span>
                      </div>
                    </div>
                    {/* Allow switching to manual if they want a different school */}
                    {!orgHasBlastSchoolId && (
                      <button
                        type="button"
                        onClick={handleManualSearch}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
                      >
                        Choose a different school
                      </button>
                    )}
                  </>
                )}

                {/* Mode: Suggested (org exists, no blastSchoolId, searching Blast) */}
                {schoolMode === 'suggested' && (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      We searched Blast for &ldquo;{schoolName}&rdquo;
                      {schoolState && ` in ${schoolState}`}.
                      Select a match or search manually.
                    </p>

                    {isSearchingSuggestions ? (
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-4 justify-center">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Searching Blast...</span>
                      </div>
                    ) : suggestedSchools.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {suggestedSchools.map((school) => (
                          <button
                            key={school.id}
                            type="button"
                            onClick={() => handlePickSuggestion(school)}
                            className="w-full text-left border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-blast-400 hover:bg-blast-50 dark:hover:bg-blast-900/10 transition-colors flex items-start gap-3"
                          >
                            <Search className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                                {school.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {school.city}, {school.state}
                                <span className="text-gray-400 ml-2">#{school.id}</span>
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        No matches found in Blast.
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={handleManualSearch}
                      className="w-full text-left border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:border-blast-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                    >
                      <Search className="h-4 w-4" />
                      None of these &mdash; search manually
                    </button>
                  </>
                )}

                {/* Mode: Manual (no org, or user chose manual from suggestions) */}
                {schoolMode === 'manual' && (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Select or enter the school for this conversion.
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Search Blast Schools
                      </label>
                      <BlastSchoolTypeahead
                        value={selectedBlastSchool}
                        onChange={(school) => {
                          setSelectedBlastSchool(school);
                          if (school) {
                            setSchoolName(school.name);
                            setSchoolCity(school.city);
                            setSchoolState(school.state);
                          }
                        }}
                      />
                    </div>

                    <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-3 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          Pre-filled from CRM. Please verify and fill any missing fields.
                        </p>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            School Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={schoolName}
                            onChange={(e) => setSchoolName(e.target.value)}
                            className="input text-sm"
                            placeholder="School name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={schoolAddress}
                            onChange={(e) => setSchoolAddress(e.target.value)}
                            className="input text-sm"
                            placeholder="Street address"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              City <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={schoolCity}
                              onChange={(e) => setSchoolCity(e.target.value)}
                              className="input text-sm"
                              placeholder="City"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              State <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={schoolState}
                              onChange={(e) => setSchoolState(e.target.value.toUpperCase())}
                              className="input text-sm"
                              placeholder="TX"
                              maxLength={2}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              ZIP <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={schoolZip}
                              onChange={(e) => setSchoolZip(e.target.value)}
                              className="input text-sm"
                              placeholder="78701"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            School Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={schoolType}
                            onChange={(e) => setSchoolType(e.target.value as 'highschool' | 'college')}
                            className="input text-sm"
                          >
                            <option value="highschool">High School</option>
                            <option value="college">College</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ==================== TEAM STEP ==================== */}
            {currentStep === 'team' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedBlastSchool
                    ? `Select a team at ${selectedBlastSchool.name} or create a new one.`
                    : 'Enter the sport and team information.'}
                </p>

                {isLoadingTeams ? (
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-4 justify-center">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading Blast teams...</span>
                  </div>
                ) : (
                  <>
                    {/* Show existing teams if any */}
                    {(pickerTeams.length > 0 || selectedBlastTeam) && !isNewTeam && (
                      <>
                        {selectedBlastTeam && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Selected
                            </p>
                            <div className="border-2 border-blast-500 bg-blast-50 dark:bg-blast-900/20 rounded-lg p-3 flex items-center gap-3">
                              <Check className="h-4 w-4 text-blast-600 dark:text-blast-400 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                                  {selectedBlastTeam.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {[selectedBlastTeam.level_name, blastGenderLabel(selectedBlastTeam.gender), `${selectedBlastTeam.school_year - 1}–${selectedBlastTeam.school_year}`].filter(Boolean).join(' · ')}
                                  <span className="text-gray-400 ml-2">#{selectedBlastTeam.id}</span>
                                </p>
                              </div>
                              {currentTeams.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedBlastTeam(null)}
                                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline flex-shrink-0"
                                >
                                  Change
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {pickerTeams.filter(t => t.id !== selectedBlastTeam?.id).length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              {selectedBlastTeam ? 'Other teams' : 'Existing teams at this school'}
                            </p>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {pickerTeams
                                .filter((team) => team.id !== selectedBlastTeam?.id)
                                .map((team) => (
                                  <button
                                    key={team.id}
                                    data-testid="convert-team-option"
                                    data-team-id={team.id}
                                    type="button"
                                    onClick={() => handlePickTeam(team)}
                                    className="w-full text-left border border-gray-200 dark:border-gray-700 rounded-lg p-3 transition-colors flex items-center gap-3 hover:border-blast-400 hover:bg-blast-50 dark:hover:bg-blast-900/10"
                                  >
                                    <div className="ml-7 min-w-0 flex-1">
                                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                                        {team.name}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {[team.level_name, blastGenderLabel(team.gender), `${team.school_year - 1}–${team.school_year}`].filter(Boolean).join(' · ')}
                                        <span className="text-gray-400 ml-2">#{team.id}</span>
                                      </p>
                                    </div>
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {!isNewTeam && hasMoreCurrentTeams && (
                      <button
                        type="button"
                        data-testid="convert-show-all-current"
                        onClick={() => setShowAllCurrentTeams((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
                      >
                        {showAllCurrentTeams
                          ? `Show only ${selectedSportName || 'this sport'} teams`
                          : `Show all current teams (${currentTeams.length})`}
                      </button>
                    )}

                    {/* New team button (when teams exist but user wants new) */}
                    {(pickerTeams.length > 0 || selectedBlastTeam) && !isNewTeam && (
                      <button
                        type="button"
                        onClick={handleNewTeam}
                        className="w-full text-left border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:border-blast-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                      >
                        <Zap className="h-4 w-4" />
                        New sport / team
                      </button>
                    )}

                    {/* Back to team list button when in new team mode */}
                    {currentTeams.length > 0 && isNewTeam && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsNewTeam(false);
                          setSelectedBlastTeam(null);
                        }}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
                      >
                        Back to existing teams
                      </button>
                    )}

                    {/* New team form: shown when no teams exist or user clicked "New sport/team" */}
                    {(isNewTeam || (pickerTeams.length === 0 && !selectedBlastTeam)) && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Sport <span className="text-red-500">*</span>
                          </label>
                          {isLoadingSports ? (
                            <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading sports...
                            </div>
                          ) : (
                            <select
                              value={selectedSportName}
                              onChange={(e) => setSelectedSportName(e.target.value)}
                              className="input"
                            >
                              <option value="">Select a sport...</option>
                              {sportSelectOptions.map((s) => (
                                <option key={s.blastSportId} value={s.name}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Level <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={selectedLevel}
                              onChange={(e) => setSelectedLevel(e.target.value)}
                              className="input"
                            >
                              {levelOptions.map((l) => (
                                <option key={l.value} value={l.value}>
                                  {l.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Gender <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={selectedGender}
                              onChange={(e) => setSelectedGender(e.target.value)}
                              className="input"
                            >
                              {genderOptions.map((g) => (
                                <option key={g.value} value={g.value}>
                                  {g.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            School Year
                          </label>
                          <select
                            value={selectedSchoolYear}
                            onChange={(e) => setSelectedSchoolYear(Number(e.target.value))}
                            className="input"
                          >
                            {(() => {
                              const now = new Date();
                              const currentAcademic = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
                              return [currentAcademic, currentAcademic + 1].map((y) => (
                                <option key={y} value={y}>
                                  {y - 1}–{y}
                                </option>
                              ));
                            })()}
                          </select>
                        </div>

                        {/* Preview line */}
                        {selectedSportName && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded px-3 py-2">
                            Will create: {LEVEL_OPTIONS.find(l => l.value === selectedLevel)?.label} {selectedSportName}{blastGenderLabel(selectedGender) ? ` (${blastGenderLabel(selectedGender)})` : ''} — {selectedSchoolYear - 1}–{selectedSchoolYear}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ==================== COACH STEP ==================== */}
            {currentStep === 'coach' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Confirm the coach contact information.
                </p>

                {/* Blast user check status */}
                {isCheckingCoach ? (
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Checking Blast for existing user...</span>
                  </div>
                ) : existingBlastUser ? (
                  <div data-testid="convert-coach-status" data-status="existing" className="border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg p-3 flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-green-800 dark:text-green-300">
                        Already in Blast as {existingBlastUser.first_name} {existingBlastUser.last_name} (User #{existingBlastUser.id})
                      </p>
                      <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">
                        Will be linked, not created.
                      </p>
                    </div>
                  </div>
                ) : email ? (
                  <div data-testid="convert-coach-status" data-status="new" className="border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 flex items-start gap-2">
                    <Zap className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      New user will be created in Blast.
                    </p>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      data-testid="convert-coach-firstname"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="convert-coach-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="input"
                    placeholder="Optional"
                  />
                </div>
              </div>
            )}

            {/* ==================== REVIEW STEP ==================== */}
            {currentStep === 'review' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Review the conversion details before submitting.
                </p>

                <div className="space-y-3">
                  {/* School */}
                  <div
                    className={`rounded-lg p-4 text-sm ${
                      selectedBlastSchool
                        ? 'border-l-4 border-l-green-500 bg-gray-50 dark:bg-gray-800/50'
                        : 'border-l-4 border-l-blue-500 bg-gray-50 dark:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-500 dark:text-gray-400 font-medium">School</span>
                      {selectedBlastSchool ? (
                        <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded font-medium">
                          Linking to existing (#{selectedBlastSchool.id})
                        </span>
                      ) : (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-medium">
                          Creating new
                        </span>
                      )}
                    </div>
                    <span className="text-gray-900 dark:text-gray-100">
                      {schoolName} ({schoolCity}, {schoolState})
                    </span>
                    {!selectedBlastSchool && schoolAddress && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {schoolAddress}, {schoolZip} · {schoolType === 'college' ? 'College' : 'High School'}
                      </p>
                    )}
                  </div>

                  {/* Team */}
                  <div
                    className={`rounded-lg p-4 text-sm ${
                      (selectedBlastTeam && !isNewTeam)
                        ? 'border-l-4 border-l-green-500 bg-gray-50 dark:bg-gray-800/50'
                        : 'border-l-4 border-l-blue-500 bg-gray-50 dark:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-500 dark:text-gray-400 font-medium">Team</span>
                      {(selectedBlastTeam && !isNewTeam) ? (
                        <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded font-medium">
                          Linking to existing (#{selectedBlastTeam.id})
                        </span>
                      ) : (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-medium">
                          Creating new
                        </span>
                      )}
                    </div>
                    <span className="text-gray-900 dark:text-gray-100">
                      {(selectedBlastTeam && !isNewTeam)
                        ? [selectedBlastTeam.level_name, blastGenderLabel(selectedBlastTeam.gender), `${selectedBlastTeam.school_year - 1}–${selectedBlastTeam.school_year}`].filter(Boolean).join(' · ')
                        : [`${LEVEL_OPTIONS.find(l => l.value === selectedLevel)?.label} ${selectedSportName}`, blastGenderLabel(selectedGender), `${selectedSchoolYear - 1}–${selectedSchoolYear}`].filter(Boolean).join(' · ')
                      }
                    </span>
                  </div>

                  {/* Coach */}
                  <div
                    className={`rounded-lg p-4 text-sm ${
                      existingBlastUser
                        ? 'border-l-4 border-l-green-500 bg-gray-50 dark:bg-gray-800/50'
                        : 'border-l-4 border-l-blue-500 bg-gray-50 dark:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-500 dark:text-gray-400 font-medium">Coach</span>
                      {existingBlastUser ? (
                        <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded font-medium">
                          Linking to existing (#{existingBlastUser.id})
                        </span>
                      ) : (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-medium">
                          Creating new
                        </span>
                      )}
                    </div>
                    <span className="text-gray-900 dark:text-gray-100">
                      {firstName} {lastName} &mdash; {email}
                      {phone && ` &mdash; ${phone}`}
                    </span>
                  </div>
                </div>

                {isConverting && (
                  <div className="flex items-center gap-2 text-blast-600 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Converting...
                  </div>
                )}
              </div>
            )}

            {/* ==================== RESULT STEP ==================== */}
            {currentStep === 'result' && (
              <div className="space-y-4">
                {convertResult && (
                  <div data-testid="convert-result-success" className="text-center py-4">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Conversion Successful
                    </h3>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-left text-sm space-y-1.5">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">School: </span>
                        <span className="font-mono text-gray-900 dark:text-gray-100">
                          {convertResult.school.name} (#{convertResult.school.id})
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Team ID: </span>
                        <span className="font-mono text-gray-900 dark:text-gray-100">
                          #{convertResult.team.id}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Coach: </span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {convertResult.coach.email}
                          {convertResult.coach.id && ` (Blast #${convertResult.coach.id})`}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Invited: </span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {convertResult.coach.invited ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {convertError && (
                  <div className="text-center py-4">
                    <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Conversion Failed
                    </h3>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-sm text-red-800 dark:text-red-300">
                      {convertError}
                    </div>
                    {errorCode && (
                      <p className="text-xs text-gray-400 mt-2">Error code: {errorCode}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between">
            {currentStep === 'result' ? (
              <div className="flex-1 flex justify-end">
                <button onClick={handleClose} className="btn-primary">
                  {convertResult ? 'Done' : 'Close'}
                </button>
              </div>
            ) : (
              <>
                <button
                  data-testid="convert-footer-back"
                  type="button"
                  onClick={currentStepIndex === 0 ? handleClose : handleBack}
                  className="btn-secondary"
                  disabled={isConverting}
                >
                  {currentStepIndex === 0 ? (
                    'Cancel'
                  ) : (
                    <>
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Back
                    </>
                  )}
                </button>
                <button
                  data-testid="convert-footer-next"
                  type="button"
                  onClick={handleNext}
                  className="btn-primary"
                  disabled={!canGoNext() || isConverting}
                >
                  {isConverting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Converting...
                    </>
                  ) : currentStep === 'review' ? (
                    <>
                      <Zap className="h-4 w-4 mr-1" />
                      Convert
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
